import axios from 'axios';

// Simple token holder to avoid circular imports with the store
let authToken = null;

export function setAuthToken(token) {
  authToken = token;
}

const api = axios.create({
  baseURL: process.env.VUE_APP_API_BASE_URL || 'http://localhost:3000/api',
  headers: {
    'Content-Type': 'application/json',
  },
});

api.interceptors.request.use((config) => {
  if (authToken) {
    config.headers.Authorization = `Bearer ${authToken}`;
  }
  return config;
});

api.interceptors.response.use(
  (response) => response,
  (error) => {
    // Normalize error shape a bit
    if (error.response && error.response.data) {
      return Promise.reject(error.response.data);
    }
    return Promise.reject({
      success: false,
      message: error.message || 'Network error',
    });
  },
);

// Auth endpoints
export const authApi = {
  register(payload) {
    return api.post('/auth/register', payload);
  },
  login(payload) {
    return api.post('/auth/login', payload);
  },
  getProfile() {
    return api.get('/auth/profile');
  },
  updateProfile(payload) {
    return api.put('/auth/profile', payload);
  },
  changePassword(payload) {
    return api.put('/auth/change-password', payload);
  },
  forgotPassword(payload) {
    return api.post('/auth/forgot-password', payload);
  },
  resetPassword(payload) {
    return api.post('/auth/reset-password', payload);
  },
};

// Project endpoints
export const projectsApi = {
  list(params) {
    return api.get('/projects', { params });
  },
  get(id) {
    return api.get(`/projects/${id}`);
  },
  create(payload) {
    // support both JSON and multipart/form-data
    if (payload instanceof FormData) {
      return api.post('/projects', payload, {
        headers: { 'Content-Type': 'multipart/form-data' },
      });
    }
    return api.post('/projects', payload);
  },
  update(id, payload) {
    // support both JSON and multipart/form-data
    if (payload instanceof FormData) {
      return api.put(`/projects/${id}`, payload, {
        headers: { 'Content-Type': 'multipart/form-data' },
      });
    }
    return api.put(`/projects/${id}`, payload);
  },
  remove(id) {
    return api.delete(`/projects/${id}`);
  },
};

// Record endpoints
export const recordsApi = {
  list(params) {
    return api.get('/records', { params });
  },
  get(id) {
    return api.get(`/records/${id}`);
  },
  create(payload) {
    return api.post('/records', payload);
  },
  update(id, payload) {
    return api.put(`/records/${id}`, payload);
  },
  remove(id) {
    return api.delete(`/records/${id}`);
  },
  uploadSnapshot(id, formData) {
    return api.post(`/records/${id}/snapshot`, formData, {
      headers: { 'Content-Type': 'multipart/form-data' },
    });
  },
};

// Building endpoints
export const buildingsApi = {
  list(params) {
    return api.get('/buildings', { params });
  },
  get(id) {
    return api.get(`/buildings/${id}`);
  },
  create(payload) {
    return api.post('/buildings', payload);
  },
  update(id, payload) {
    return api.put(`/buildings/${id}`, payload);
  },
  remove(id) {
    return api.delete(`/buildings/${id}`);
  },
};

// Floor endpoints
export const floorsApi = {
  list(params) {
    return api.get('/floors', { params });
  },
  get(id) {
    return api.get(`/floors/${id}`);
  },
  create(payload) {
    return api.post('/floors', payload);
  },
  update(id, payload) {
    return api.put(`/floors/${id}`, payload);
  },
  remove(id) {
    return api.delete(`/floors/${id}`);
  },
};

// Room endpoints
export const roomsApi = {
  list(params) {
    return api.get('/rooms', { params });
  },
  get(id) {
    return api.get(`/rooms/${id}`);
  },
  create(payload) {
    return api.post('/rooms', payload);
  },
  update(id, payload) {
    return api.put(`/rooms/${id}`, payload);
  },
  remove(id) {
    return api.delete(`/rooms/${id}`);
  },
};

// Reports endpoints
export const reportsApi = {
  summary(params) {
    return api.get('/reports/summary', { params });
  },
  records(params) {
    return api.get('/reports/records', { params });
  },
  projects(params) {
    return api.get('/reports/projects', { params });
  },
  timeBased(params) {
    return api.get('/reports/time-based', { params });
  },
  users(params) {
    return api.get('/reports/users', { params });
  },
};

// Admin endpoints
export const adminApi = {
  // users
  listUsers(params) {
    return api.get('/admin/users', { params });
  },
  getUser(id) {
    return api.get(`/admin/users/${id}`);
  },
  createUser(payload) {
    return api.post('/admin/users', payload);
  },
  updateUser(id, payload) {
    return api.put(`/admin/users/${id}`, payload);
  },
  resetUserPassword(id, payload) {
    return api.put(`/admin/users/${id}/reset-password`, payload);
  },
  freezeUser(id) {
    return api.put(`/admin/users/${id}/freeze`);
  },
  unfreezeUser(id) {
    return api.put(`/admin/users/${id}/unfreeze`);
  },
  suspendUser(id, payload) {
    return api.put(`/admin/users/${id}/suspend`, payload);
  },
  unsuspendUser(id) {
    return api.put(`/admin/users/${id}/unsuspend`);
  },
  activateUser(id) {
    return api.put(`/admin/users/${id}/activate`);
  },
  deactivateUser(id) {
    return api.put(`/admin/users/${id}/deactivate`);
  },
  deleteUser(id) {
    return api.delete(`/admin/users/${id}`);
  },
  assignSupervisorToUser(id, payload) {
    return api.put(`/admin/users/${id}/assign-supervisor`, payload);
  },

  // supervisors
  listSupervisors(params) {
    return api.get('/admin/supervisors', { params });
  },

  // projects
  listProjects(params) {
    return api.get('/admin/projects', { params });
  },
  getProject(id) {
    return api.get(`/admin/projects/${id}`);
  },
  updateProject(id, payload) {
    return api.put(`/admin/projects/${id}`, payload);
  },
  updateProjectStatus(id, payload) {
    return api.put(`/admin/projects/${id}/status`, payload);
  },
  addProjectFeedback(id, payload) {
    return api.put(`/admin/projects/${id}/feedback`, payload);
  },

  // records
  listRecords(params) {
    return api.get('/admin/records', { params });
  },
  getRecord(id) {
    return api.get(`/admin/records/${id}`);
  },
  updateRecord(id, payload) {
    return api.put(`/admin/records/${id}`, payload);
  },
  updateRecordStatus(id, payload) {
    return api.put(`/admin/records/${id}/status`, payload);
  },
  addRecordFeedback(id, payload) {
    return api.put(`/admin/records/${id}/feedback`, payload);
  },
};

// Supervisor endpoints
export const supervisorApi = {
  addUser(payload) {
    return api.post('/supervisor/users', payload);
  },
  listUsers(params) {
    return api.get('/supervisor/users', { params });
  },
  statistics(params) {
    return api.get('/supervisor/statistics', { params });
  },
  userStatistics(userId, params) {
    return api.get(`/supervisor/users/${userId}/statistics`, { params });
  },
  listProjects(params) {
    return api.get('/supervisor/projects', { params });
  },
  listRecords(params) {
    return api.get('/supervisor/records', { params });
  },
  updateProjectStatus(id, payload) {
    return api.put(`/supervisor/projects/${id}/status`, payload);
  },
  addProjectFeedback(id, payload) {
    return api.put(`/supervisor/projects/${id}/feedback`, payload);
  },
  updateProject(id, payload) {
    return api.put(`/supervisor/projects/${id}`, payload);
  },
  updateRecordStatus(id, payload) {
    return api.put(`/supervisor/records/${id}/status`, payload);
  },
  addRecordFeedback(id, payload) {
    return api.put(`/supervisor/records/${id}/feedback`, payload);
  },
  updateRecord(id, payload) {
    return api.put(`/supervisor/records/${id}`, payload);
  },
};

export default api;


